import React, { useEffect, useState } from "react";
import { Modal, Table, Tag, Button, Popconfirm, message } from "antd";
import { DownloadOutlined, RollbackOutlined } from "@ant-design/icons";
import {
  getSpecVersions,
  downloadSpecVersion,
  restoreSpecVersion,
} from "../../service/fsd.service";

// Lists every recorded version of a control document with download + restore.
function SpecVersionHistoryModal({ open, record, onClose, onRestored }) {
  const specName = record?.specName;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    if (!specName) return;
    setLoading(true);
    getSpecVersions(specName)
      .then((res) => setRows(res.data || []))
      .catch(() => message.error("Could not load version history."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, specName]);

  const download = async (v) => {
    setBusyId(v.id);
    try {
      const buf = await downloadSpecVersion(v.id);
      const url = URL.createObjectURL(new Blob([buf]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${specName}-v${v.versionNo}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error("Download failed.");
    } finally {
      setBusyId(null);
    }
  };

  const restore = async (v) => {
    setBusyId(v.id);
    try {
      await restoreSpecVersion(v.id);
      message.success(`Restored version ${v.versionNo} as the current spec.`);
      load();
      onRestored?.();
    } catch (e) {
      message.error(e?.response?.data?.message || "Restore failed.");
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    {
      title: "Version",
      dataIndex: "versionNo",
      key: "versionNo",
      width: 110,
      render: (v, r) => (
        <span>
          v{v}
          {r.current && (
            <Tag color="#3B6D11" style={{ marginLeft: 8 }}>
              Current
            </Tag>
          )}
        </span>
      ),
    },
    { title: "Uploaded", dataIndex: "uploadedDate", key: "uploadedDate", width: 170 },
    { title: "By", dataIndex: "uploadedBy", key: "uploadedBy", ellipsis: true },
    { title: "Note", dataIndex: "note", key: "note", ellipsis: true, render: (v) => v || "—" },
    {
      title: "",
      key: "ops",
      width: 230,
      fixed: "right",
      render: (_, v) => (
        <span style={{ whiteSpace: "nowrap" }}>
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            loading={busyId === v.id}
            onClick={() => download(v)}
          >
            Download
          </Button>
          {!v.current && (
            <Popconfirm
              title="Restore this version?"
              description="It becomes the current spec and the default suite is regenerated."
              okText="Restore"
              cancelText="Cancel"
              onConfirm={() => restore(v)}
            >
              <Button type="link" size="small" icon={<RollbackOutlined />}>
                Restore
              </Button>
            </Popconfirm>
          )}
        </span>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="90%"
      style={{ maxWidth: 960 }}
      centered
      destroyOnClose
      title={`Version history — ${specName || ""}`}
    >
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: "max-content", y: "55vh" }}
      />
    </Modal>
  );
}

export default SpecVersionHistoryModal;
